import { MembershipType } from "@app/exports/enumerations";
import {
  NPIStatus,
  ProviderActionStatusEnum,
  LocationConfidenceScoresLabelEnum
} from "@app/projects/enumerators";
import { ClaimsActivityLineOfBusinessTypeEnum, ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { CareJourneyScoresEpisode, MotiveScore, PCAPayerParticipation } from "@app/projects/results/servicing-provider/servicingProviderDetail";
import { PaginationResult, wafflesKeyColumnName } from "@app/shared/waffles/exports";
import { JSX } from "react";
import { AiResponseReviewRating } from "@app/operations/ai-response-review/enums";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { SpecialtyPredictionModelEnum } from "@app/projects/servicing-provider/provider-claims/SpecialtyPredictionModelEnum";

// Provider Hub search results plus result-set-level indicators for the Gap Impact region-status breakdown.
// The per-item useGapsByRegionStatus flags describe a single row (and are legitimately false for rows with no
// region-set data), so the grid must not infer column visibility from the items on the current page — these
// flags come from the backend for the whole result set. Optional so existing empty-result literals still type.
export interface GapImpactPaginationResult<T> extends PaginationResult<T> {
  useGapsByRegionStatus?: boolean;
  unfilteredUseGapsByRegionStatus?: boolean;
}

export interface RegionSetSpecialtyGroupsWithGapsTableData {
  [wafflesKeyColumnName]: string;
  serviceArea: string;
  specialtyGroup: string;
  providersImpactCount: JSX.Element;
}

export interface ProviderHubCountiesImpacted {
  countySSA: string;
  name: string;
  state: string;
}

export interface ProviderHubSpecialtiesImpacted {
  code: string;
  name: string;
}

export interface ProviderHubServicingProviders {
  projectRunConfigurationId: number;
  servicingProviderKey: string;
  name: string;
  npi: string;
  providerNetworkTypes: ProviderNetworkTypeEnum[];
  providerActionStatuses: ProviderActionStatusEnum[];
  gapsClosed?: number;
  activeGapsClosed?: number;
  pendingGapsClosed?: number;
  approvedGapsClosed?: number;
  opportunityGapsClosed?: number;
  useGapsByRegionStatus: boolean;
  specialtiesImpacted: ProviderHubSpecialtiesImpacted[];
  countiesImpacted: ProviderHubCountiesImpacted[];
  accessPointCount: number;
  effectivenessRank?: number;
  efficiencyRank?: number;
  totalClaimsVolume?: ClaimsActivityVolumeLabelEnum;
  commercialClaimsVolume?: ClaimsActivityVolumeLabelEnum;
  medicareClaimsVolume?: ClaimsActivityVolumeLabelEnum;
  medicaidClaimsVolume?: ClaimsActivityVolumeLabelEnum;
  vaClaimsVolume?: ClaimsActivityVolumeLabelEnum;
  tricareClaimsVolume?: ClaimsActivityVolumeLabelEnum;
  careJourneyScoresCostScore?: number;
  careJourneyScoresQualityScore?: number;
  motiveScores?: MotiveScore[];
  hasNetworkIntegrityFailure?: boolean;
  predictedSpecialtyNames?: string[];
  tins?: string[];
}

export interface AggregateServicingProvider {
  projectId: number;
  projectRunConfigurationId: number;
  servicingProviderKey: string;
  name: string;
  accessPointCount: number;
  regionSetServed: number;
  gapsClosed?: number;
  activeGapsClosed?: number;
  pendingGapsClosed?: number;
  approvedGapsClosed?: number;
  opportunityGapsClosed?: number;
  useGapsByRegionStatus: boolean;
  gapsIncreaseAccess?: number;
  activeGapsIncreaseAccess?: number;
  pendingGapsIncreaseAccess?: number;
  approvedGapsIncreaseAccess?: number;
  opportunityGapsIncreaseAccess?: number;
  npi: string;
  npiStatus: NPIStatus;
  providerNetworkTypes: ProviderNetworkTypeEnum[];
  providerActionStatuses: ProviderActionStatusEnum[];
  effectivenessRank?: number;
  efficiencyRank?: number;
  topConditions?: string[];
  topProcedures?: string[];
  topPrescriptions?: string[];
  totalClaimsVolume?: ClaimsActivityVolumeLabelEnum;
  medicareClaimsVolume?: ClaimsActivityVolumeLabelEnum;
  medicaidClaimsVolume?: ClaimsActivityVolumeLabelEnum;
  commercialClaimsVolume?: ClaimsActivityVolumeLabelEnum;
  tricareClaimsVolume?: ClaimsActivityVolumeLabelEnum;
  vaClaimsVolume?: ClaimsActivityVolumeLabelEnum;
  totalClaimAmount?: number;
  medicareClaimAmount?: number;
  medicaidClaimAmount?: number;
  commercialClaimAmount?: number;
  tricareClaimAmount?: number;
  vaClaimAmount?: number;
  totalNewPatientInd?: boolean | null;
  medicareNewPatientInd?: boolean | null;
  medicaidNewPatientInd?: boolean | null;
  commercialNewPatientInd?: boolean | null;
  tricareNewPatientInd?: boolean | null;
  vaNewPatientInd?: boolean | null;
  commercialParticipation?: PCAPayerParticipation[];
  medicaidParticipation?: PCAPayerParticipation[];
  medicareParticipation?: PCAPayerParticipation[];
  tricareParticipation?: PCAPayerParticipation[];
  vaParticipation?: PCAPayerParticipation[];
  totalParticipation?: PCAPayerParticipation[];
  motiveScores?: MotiveScore[];
  hasNetworkIntegrityFailure?: boolean;
  isNpiDeactivated: boolean;
  isNpiInvalid: boolean;
  isNpiMissing: boolean;
  hasOigExclusion: boolean;
  hasMedicareOptOut: boolean;
  hasMedicarePreclusion: boolean;
  isPractitionerLicenseExpired?: boolean;
  isProviderGhost?: boolean;
  isProviderGhostForLob?: boolean;
  ghostLineOfBusiness: string;
  isPractitionerRetired?: boolean;
  hasExcessLocations: boolean;
  hasExcessSpecialties: boolean;
  hasExcessLocationsNetwork?: boolean;
  hasExcessLocationsProspect?: boolean;
  hasExcessLocationsMarket?: boolean;
  hasExcessSpecialtiesNetwork?: boolean;
  hasExcessSpecialtiesProspect?: boolean;
  hasExcessSpecialtiesMarket?: boolean;
  careJourneyScoresCostScore?: number;
  careJourneyScoresQualityScore?: number;
  careJourneyScoresTopEpisodes?: CareJourneyScoresEpisode[];
  professionalAffiliationId?: number;
  professionalContractEntityName?: string;
  predictedSpecialtyNames?: string[];
}

export interface AggregateOrganization {
  npi: string;
  projectRunConfigurationId: number;
  name?: string;
  providerNetworkTypes: ProviderNetworkTypeEnum[];
  providerActionStatuses: ProviderActionStatusEnum[];
  gapsClosed: number;
  activeGapsClosed?: number;
  pendingGapsClosed?: number;
  approvedGapsClosed?: number;
  opportunityGapsClosed?: number;
  useGapsByRegionStatus: boolean;
  servicingProviders: number;
  servicingSpecialties: number;
  outOfNetworkServicingProviders: number;
  inNetworkServicingProviders: number;
  quality: number | null;
  cost: number | null;
  phoneNumber: string;
  dbaName: string;
  tin: string;
  specialtyCount: SpecialtyCountModel[];
}

export interface AggregateHospital {
  ccn: string;
  primaryNpi: string;
  projectRunConfigurationId: number;
  hospitalName?: string;
  facilityType: string;
  address: string;
  phoneNumber: string;
  contractEntityId: string;
  contractEntityName: string;
  contractEntityType: string;
  providerNetworkTypes: ProviderNetworkTypeEnum[];
  providerActionStatuses: ProviderActionStatusEnum[];
  gapsImpact: number;
  activeGapsImpact?: number;
  pendingGapsImpact?: number;
  approvedGapsImpact?: number;
  opportunityGapsImpact?: number;
  useGapsByRegionStatus: boolean;
  accessPointCount: number;
  servicingProviders: number;
  servicingSpecialties: number;
  inNetworkServicingProviders: number;
  outOfNetworkServicingProviders: number;
  quality: number;
  cost: number;
  specialtyCount: SpecialtyCountModel[];
  totalClaims: number;
  medicareClaims: number;
  medicaidClaims: number;
  commercialClaims: number;
}

export interface AdequacyCriticality {
  score?: number;
}

export interface MarketAlternative {
  npi: string;
  providerKey: string;
  providerName: string;
  score?: number;
}

export interface MarketProviderJobStatusResponse {
  isFailed: boolean;
  isProcessing: boolean;
}

export interface ProviderHubServicingProvidersTableData {
  [wafflesKeyColumnName]: string;
  name?: string | JSX.Element;
  identifier?: string | JSX.Element;
  providerType: JSX.Element;
  providerActionStatus: JSX.Element;
  gapsClosed: string;
  activeGapImpact: string;
  pendingGapImpact: string;
  approvedGapImpact: string;
  opportunityGapImpact: string;
  specialtiesImpacted: JSX.Element | string;
  countiesImpacted: JSX.Element | string;
  accessPointCount: number;
  effectivenessRank?: string;
  efficiencyRank?: string;
  totalClaimsVolume?: string;
  commercialClaimsVolume?: string;
  medicareClaimsVolume?: string;
  medicaidClaimsVolume?: string;
  vaClaimsVolume?: string;
  tricareClaimsVolume?: string;
  careJourneyCost?: string;
  careJourneyQuality?: string;
  motiveScores?: JSX.Element;
}

export interface OrganizationProfileKey {
  readonly npi2: string;
  readonly contractEntityId: number;
}

export interface AffiliationProfileAvailability {
  readonly contractEntityIdsWithProfile: readonly number[];
  readonly organizationsWithProfile: readonly OrganizationProfileKey[];
}

export interface AggregateContractEntity {
  contractEntityId: number;
  projectRunConfigurationId: number;
  name?: string;
  entityType?: string;
  providerNetworkTypes: ProviderNetworkTypeEnum[];
  providerActionStatuses: ProviderActionStatusEnum[];
  gapsClosed: number;
  activeGapsClosed?: number;
  pendingGapsClosed?: number;
  approvedGapsClosed?: number;
  opportunityGapsClosed?: number;
  useGapsByRegionStatus: boolean;
  filteredServicingProviders: number;
  filteredServicingSpecialties: ProviderHubSpecialtiesImpacted[];
  filteredOutOfNetworkServicingProviders: number;
  filteredInNetworkServicingProviders: number;
  filteredQuality: number | null;
  filteredCost: number | null;
  careJourneyQuality: number | null;
  careJourneyCost: number | null;
  phoneNumber: string;
  specialtyCount: SpecialtyCountModel[];
  facilities: ProviderHubFacilityModel[];
  tins: ProviderHubTinModel[];
}

export interface ProviderHubContractEntityTableData {
  [wafflesKeyColumnName]: string;
  name?: string|  JSX.Element;
  entityType?: string;
  filteredProviderSources: JSX.Element;
  filteredStatuses: JSX.Element;
  gapsClosed: number | string;
  activeGapImpact?: number | string;
  pendingGapImpact?: number | string;
  approvedGapImpact?: number | string;
  opportunityGapImpact?: number | string;
  filteredServicingProviders: number;
  filteredServicingSpecialties: JSX.Element | string;
  filteredOutOfNetworkServicingProviders: number;
  filteredInNetworkServicingProviders: number;
  filteredQuality: string;
  filteredCost: string;
  unfilteredProviderSources?: JSX.Element | JSX.Element;
  unfilteredStatuses?: JSX.Element;
  unfilteredgapsClosed?: number | JSX.Element;
  unfilteredActiveGapImpact?: number | string;
  unfilteredPendingGapImpact?: number | string;
  unfilteredApprovedGapImpact?: number | string;
  unfilteredOpportunityGapImpact?: number | string;
  unfilteredServicingProviders?: number | JSX.Element;
  unfilteredServicingSpecialties?: JSX.Element | string;
  unfilteredOutOfNetworkServicingProviders?: number | JSX.Element;
  unfilteredInNetworkServicingProviders?: number | JSX.Element;
  unfilteredQuality?: string | JSX.Element;
  filteredMotiveAppropriateness?: string | JSX.Element;
  unfilteredMotiveAppropriateness?: string | JSX.Element;
  filteredCareJourneyCost?: string | JSX.Element;
  unfilteredCareJourneyCost?: string | JSX.Element;
  filteredCareJourneyQuality?: string | JSX.Element;
  unfilteredCareJourneyQuality?: string | JSX.Element;
}

export interface ProviderHubContractEntityProvidersTableData {
 [wafflesKeyColumnName]: string;
  nameElement: JSX.Element;
  name?: string;
  identifier: string;
  providerNetworkTypes: JSX.Element;
  providerActionStatuses: JSX.Element;
  filteredStatuses: string[];
  affiliationStrength?: number;
  gapsClosed?: number | string;
  gapImpact?: number | string;
  activeGapImpact?: number | string;
  pendingGapImpact?: number | string;
  approvedGapImpact?: number | string;
  opportunityGapImpact?: number | string;
  specialtiesImpacted: JSX.Element | string;
  countiesImpacted: JSX.Element | string;
  accessPoints?: number;
  effectivenessRank?: number;
  efficiencyRank?: number;
  totalClaimVolume?: ClaimsActivityVolumeLabelEnum;
  medicaidClaimVolume?: ClaimsActivityVolumeLabelEnum;
  medicareClaimVolume?: ClaimsActivityVolumeLabelEnum;
  commercialClaimVolume?: ClaimsActivityVolumeLabelEnum;
  vaClaimVolume?: ClaimsActivityVolumeLabelEnum;
  tricareClaimVolume?: number;
  motiveAppropriateness?: number | string;
  careJourneyCost?: number | string;
  careJourneyQuality?: number | string;
  contractEntity?: JSX.Element | string;
  organization?: JSX.Element | string;
  predictedSpecialtyNames?: string[];
}

export interface ProviderHubContractEntityOrganizationsTableData {
  [wafflesKeyColumnName]: string;
  npiElement: string | JSX.Element;
  npi: string;
  name: string,
  filteredProviderSources: JSX.Element;
  filteredStatusesElement: JSX.Element;
  filteredStatuses: string[];
  affiliationStrength: number;
  gapsClosed: number;
  servicingProviders: number;
  servicingSpecialties: number;
  outOfNetworkServicingProviders: number;
  inNetworkServicingProviders: number;
  quality: string;
  cost: string;
}

export interface ProviderHubContractEntities {
  contractEntityId: number;
  projectRunConfigurationId: number;
  name?: string;
  entityType?: string;
  providerNetworkTypes: ProviderNetworkTypeEnum[];
  providerActionStatuses: ProviderActionStatusEnum[];
  unfilteredProviderNetworkTypes: ProviderNetworkTypeEnum[];
  unfilteredProviderActionStatuses: ProviderActionStatusEnum[];
  gapsClosed?: number;
  unfilteredGapsClosed?: number;
  activeGapsClosed?: number;
  pendingGapsClosed?: number;
  approvedGapsClosed?: number;
  opportunityGapsClosed?: number;
  useGapsByRegionStatus: boolean;
  unfilteredActiveGapsClosed?: number;
  unfilteredPendingGapsClosed?: number;
  unfilteredApprovedGapsClosed?: number;
  unfilteredOpportunityGapsClosed?: number;
  unfilteredUseGapsByRegionStatus: boolean;
  filteredServicingProviders?: number;
  unfilteredServicingProviders?: number;
  filteredServicingSpecialties?: ProviderHubSpecialtiesImpacted[];
  unfilteredServicingSpecialties?: ProviderHubSpecialtiesImpacted[];
  filteredOutOfNetworkServicingProviders?: number;
  unfilteredOutOfNetworkServicingProviders?: number;
  filteredInNetworkServicingProviders?: number;
  unfilteredInNetworkServicingProviders?: number;
  filteredQuality?: number;
  unfilteredQuality?: number;
  filteredCost?: number;
  unfilteredCost?: number;
  filteredMotiveAppropriateness?: string | JSX.Element;
  unfilteredMotiveAppropriateness?: string | JSX.Element;
  filteredCareJourneyCost?: string | JSX.Element;
  unfilteredCareJourneyCost?: string | JSX.Element;
  filteredCareJourneyQuality?: string | JSX.Element;
  unfilteredCareJourneyQuality?: string | JSX.Element;
}

export interface ProviderHubContractEntityProviders {
  name?: string;
  npi: string;
  servicingProviderKey: string;
  providerNetworkTypes: ProviderNetworkTypeEnum[];
  providerActionStatuses: ProviderActionStatusEnum[];
  affiliationStrength?: number;
  gapsClosed?: number;
  activeGapsClosed?: number;
  pendingGapsClosed?: number;
  approvedGapsClosed?: number;
  opportunityGapsClosed?: number;
  useGapsByRegionStatus?: boolean;
  specialtiesImpacted: ProviderHubSpecialtiesImpacted[];
  countiesImpacted: ProviderHubCountiesImpacted[];
  accessPointCount?: number;
  effectivenessRank?: number;
  efficiencyRank?: number;
  totalClaimVolume?: ClaimsActivityVolumeLabelEnum;
  medicaidClaimVolume?: ClaimsActivityVolumeLabelEnum;
  medicareClaimVolume?: ClaimsActivityVolumeLabelEnum;
  commercialClaimVolume?: ClaimsActivityVolumeLabelEnum;
  vaClaimVolume?: ClaimsActivityVolumeLabelEnum;
  tricareClaimVolume?: ClaimsActivityVolumeLabelEnum;
  hasNetworkIntegrityFailure?: boolean;
  careJourneyCost?: number;
  careJourneyQuality?: number;
  motiveAppropriateness?: number;
  professionalContractEntityName?: string;
  professionalContractEntityId?: number;
  professionalOrganizationNpi?: string;
  professionalOrganizationName?: string;
  professionalOrganizationContractEntityId?: number;
  predictedSpecialtyNames?: string[];
  // Facilities tab: type-2 facility records carry the hospital CCN they roll up to (populated by the
  // backend once the grouped Facilities endpoint lands; used to group facilities by CCN and link to the Hospital profile).
  ccn?: string;
  hospitalName?: string;
}

export interface ProviderHubContractEntityOrganizations {
  name?: string;
  npi: string;
  providerNetworkTypes: ProviderNetworkTypeEnum[];
  providerActionStatuses: ProviderActionStatusEnum[];
  affiliationStrength: number;
  gapsClosed: number;
  servicingProviders: number;
  servicingSpecialties: number;
  outOfNetworkServicingProviders: number;
  inNetworkServicingProviders: number;
  quality: string;
  cost: string;
}

export interface SpecialtyCountModel {
  key: string,
  name: string;
  count: number;
  code: string;
}

export interface ProviderHubFacilityModel {
  key: string,
  name: string;
  address: string;
}

export interface ProviderHubTinModel {
  key: string,
  name: string;
  id: string;
}

export interface Procedure {
  code: string;
  description: string;
}

export interface Condition {
  code: string;
  description: string;
}

export interface ServicingAccessPoint {
  servicingAccessPointId: number;
  providerNetworkType: ProviderNetworkTypeEnum;
  providerActionStatus: ProviderActionStatusEnum;
  providerId: number;
  providerName: string;
  organizationName: string;
  specialtyGroupCode: string;
  specialtyGroupName: string;
  specialtyCode?: string;
  specialtyDescription?: string;
  countyName: string;
  address: string;
  address2?: string;
  state: string;
  city: string;
  zip: string;
  npi: string;
  tin: string;
  locationConfidenceScore?: LocationConfidenceScoresLabelEnum;
  practitionerAtLocationConfidence?: string;
  hasLeftOffice?: boolean;
  isNotRecognized?: boolean;
  isNoLongerOnRoster?: boolean;
  isTermedOnRoster?: boolean;
  hasLocationClosed?: boolean;
  hasNetworkIntegrityFailure?: boolean;
  taxonomyMismatch?: string;
  isProviderUniverseProviderSet?: boolean;
  specialtyPredictionModel?: SpecialtyPredictionModelEnum;
}

export interface AccessPointFilterState {
  actionStatuses: ProviderActionStatusEnum[];
  locationConfidences: (LocationConfidenceScoresLabelEnum | null)[];
  providerSources: ProviderNetworkTypeEnum[];
}

export const emptyAccessPointFilterState: AccessPointFilterState = {
  actionStatuses: [],
  locationConfidences: [],
  providerSources: [],
};

export enum AccessPointSortOptionEnum {
  Default = "Default",
  LocationConfidenceHighToLow = "LocationConfidenceHighToLow",
  OutOfNetworkFirst = "OutOfNetworkFirst",
  TerminatedFirst = "TerminatedFirst",
}

export interface ProviderHubOrganizations {
  name?: string;
  npi: string;
  contractEntityId: number;
  providerNetworkTypes: ProviderNetworkTypeEnum[];
  unfilteredProviderNetworkTypes: ProviderNetworkTypeEnum[];
  providerActionStatuses: ProviderActionStatusEnum[];
  unfilteredProviderActionStatuses: ProviderActionStatusEnum[];
  gapsClosed?: number;
  unfilteredGapsClosed?: number;
  activeGapsClosed?: number;
  pendingGapsClosed?: number;
  approvedGapsClosed?: number;
  opportunityGapsClosed?: number;
  useGapsByRegionStatus: boolean;
  unfilteredActiveGapsClosed?: number;
  unfilteredPendingGapsClosed?: number;
  unfilteredApprovedGapsClosed?: number;
  unfilteredOpportunityGapsClosed?: number;
  unfilteredUseGapsByRegionStatus: boolean;
  servicingProviders?: number;
  unfilteredServicingProviders?: number;
  filteredServicingSpecialties?: ProviderHubSpecialtiesImpacted[];
  unfilteredServicingSpecialties?: ProviderHubSpecialtiesImpacted[];
  outOfNetworkServicingProviders?: number;
  unfilteredOutOfNetworkServicingProviders?: number;
  inNetworkServicingProviders?: number;
  unfilteredInNetworkServicingProviders?: number;
  quality?: number;
  unfilteredQuality?: number;
  cost?: number;
  unfilteredCost?: number;
  motiveAppropriateness?: number;
  unfilteredMotiveAppropriateness?: number;
  careJourneyCost?: number;
  unfilteredCareJourneyCost?: number;
  careJourneyQuality?: number;
  unfilteredCareJourneyQuality?: number;
}

// NPI type-2 organization servicing a hospital (CCN), rendered in the hospital profile "Services" table.
// Kept distinct from ProviderHubOrganizations so hospital-only fields (access points, counties impacted)
// don't leak into the organization search grid model.
export interface HospitalService {
  name?: string;
  npi: string;
  contractEntityId: number;
  providerNetworkTypes: ProviderNetworkTypeEnum[];
  providerActionStatuses: ProviderActionStatusEnum[];
  gapsClosed?: number;
  activeGapsClosed?: number;
  pendingGapsClosed?: number;
  approvedGapsClosed?: number;
  opportunityGapsClosed?: number;
  useGapsByRegionStatus?: boolean;
  servicingProviders?: number;
  inNetworkServicingProviders?: number;
  outOfNetworkServicingProviders?: number;
  accessPointCount?: number;
  filteredServicingSpecialties?: ProviderHubSpecialtiesImpacted[];
  quality?: number;
  cost?: number;
  // Claims columns are ClaimsActivityVolumeLabelEnum buckets serialized to their label (e.g. "Standard"), not counts.
  totalClaims?: string;
  medicareClaims?: string;
  medicaidClaims?: string;
  commercialClaims?: string;
  countiesImpacted?: ProviderHubCountiesImpacted[];
}

export interface ProviderHubOrganizationTableData {
  [wafflesKeyColumnName]: string;
  nameElement: JSX.Element;
  name?: string;
  npi2: string;
  providerSources: JSX.Element;
  unfilteredProviderSources: JSX.Element;
  statuses: JSX.Element;
  unfilteredStatuses: JSX.Element;
  gapsClosed?: number | string;
  activeGapImpact?: number | string;
  pendingGapImpact?: number | string;
  approvedGapImpact?: number | string;
  opportunityGapImpact?: number | string;
  unfilteredGapsClosed?: number | string;
  unfilteredActiveGapImpact?: number | string;
  unfilteredPendingGapImpact?: number | string;
  unfilteredApprovedGapImpact?: number | string;
  unfilteredOpportunityGapImpact?: number | string;
  servicingProviders?: number;
  unfilteredServicingProviders?: number;
  servicingSpecialties?: JSX.Element;
  unfilteredServicingSpecialties?: JSX.Element;
  outOfNetworkServicingProviders?: number;
  unfilteredOutOfNetworkServicingProviders?: number;
  inNetworkServicingProviders?: number;
  unfilteredInNetworkServicingProviders?: number;
  effectiveness?: string | number;
  unfilteredEffectiveness?: string | number;
  efficiency?: string | number;
  unfilteredEfficiency?: string | number;
  motiveAppropriateness?: string | number;
  unfilteredMotiveAppropriateness?: string | number;
  careJourneyCost?: string | number | JSX.Element;
  unfilteredCareJourneyCost?: string | number | JSX.Element;
  careJourneyQuality?: string | number | JSX.Element;
  unfilteredCareJourneyQuality?: string | number | JSX.Element;
}

export type ProviderHubOrganizationsFiltersRequestModel = {
  projectId: number;
  projectRunConfigurationId: number;
  searchByOption: ProviderSearchByEnum;
  searchByInput: string;
  regionSetIds: number[];
  specialtyGroupCodes: string[];
  providerNetworkTypes: ProviderNetworkTypeEnum[];
  providerActionStatuses: ProviderActionStatusEnum[];
  closeGapsMin: number;
  closeGapsMax: number;
  effectiveness: number[];
  efficiency: number[];
  motiveScore: number[];
  careJourneyCostScore: number[];
  careJourneyQualityScore: number[];
  effectivenessMin?: number | null;
  effectivenessMax?: number | null;
  efficiencyMin?: number | null;
  efficiencyMax?: number | null;
  motiveScoreMin?: number | null;
  motiveScoreMax?: number | null;
  careJourneyCostScoreMin?: number | null;
  careJourneyCostScoreMax?: number | null;
  careJourneyQualityScoreMin?: number | null;
  careJourneyQualityScoreMax?: number | null;
  includeUnknownEffectiveness?: boolean;
  includeUnknownEfficiency?: boolean;
  includeUnknownMotiveScore?: boolean;
  includeUnknownCareJourneyCostScore?: boolean;
  includeUnknownCareJourneyQualityScore?: boolean;
  servicingProviderCountMinimum: number;
  servicingProviderCountMaximum: number;
  sortContractEntityColumnAndOrder?:  ProviderHubContractEntitySortColumn;
  sortOrganizationColumnAndOrder: ProviderHubOrganizationSortColumn;
  pageSize: number;
  pageIndex?: number;
};

export interface ProviderHubHospitals {
  hospitalName?: string;
  ccn: string;
  primaryNpi: string;
  providerNetworkTypes: ProviderNetworkTypeEnum[];
  unfilteredProviderNetworkTypes: ProviderNetworkTypeEnum[];
  providerActionStatuses: ProviderActionStatusEnum[];
  unfilteredProviderActionStatuses: ProviderActionStatusEnum[];
  gapsImpact?: number;
  unfilteredGapsImpact?: number;
  activeGapsImpact?: number;
  pendingGapsImpact?: number;
  approvedGapsImpact?: number;
  opportunityGapsImpact?: number;
  useGapsByRegionStatus: boolean;
  unfilteredActiveGapsImpact?: number;
  unfilteredPendingGapsImpact?: number;
  unfilteredApprovedGapsImpact?: number;
  unfilteredOpportunityGapsImpact?: number;
  unfilteredUseGapsByRegionStatus: boolean;
  servicingProviders?: number;
  unfilteredServicingProviders?: number;
  filteredServicingSpecialties?: ProviderHubSpecialtiesImpacted[];
  unfilteredServicingSpecialties?: ProviderHubSpecialtiesImpacted[];
  outOfNetworkServicingProviders?: number;
  unfilteredOutOfNetworkServicingProviders?: number;
  inNetworkServicingProviders?: number;
  unfilteredInNetworkServicingProviders?: number;
  quality?: number;
  unfilteredQuality?: number;
  cost?: number;
  unfilteredCost?: number;
  motiveAppropriateness?: number;
  unfilteredMotiveAppropriateness?: number;
  careJourneyCost?: number;
  unfilteredCareJourneyCost?: number;
  careJourneyQuality?: number;
  unfilteredCareJourneyQuality?: number;
  totalClaims?: number;
  unfilteredTotalClaims?: number;
  medicareClaims?: number;
  unfilteredMedicareClaims?: number;
  medicaidClaims?: number;
  unfilteredMedicaidClaims?: number;
  commercialClaims?: number;
  unfilteredCommercialClaims?: number;
}

export interface ProviderHubHospitalTableData {
  [wafflesKeyColumnName]: string;
  nameElement: JSX.Element;
  name?: string;
  identifier: string;
  providerSources: JSX.Element;
  unfilteredProviderSources: JSX.Element;
  statuses: JSX.Element;
  unfilteredStatuses: JSX.Element;
  gapsImpact?: number | string;
  activeGapImpact?: number | string;
  pendingGapImpact?: number | string;
  approvedGapImpact?: number | string;
  opportunityGapImpact?: number | string;
  unfilteredGapsImpact?: number | string;
  unfilteredActiveGapImpact?: number | string;
  unfilteredPendingGapImpact?: number | string;
  unfilteredApprovedGapImpact?: number | string;
  unfilteredOpportunityGapImpact?: number | string;
  servicingProviders?: number;
  unfilteredServicingProviders?: number;
  servicingSpecialties?: JSX.Element;
  unfilteredServicingSpecialties?: JSX.Element;
  outOfNetworkServicingProviders?: number;
  unfilteredOutOfNetworkServicingProviders?: number;
  inNetworkServicingProviders?: number;
  unfilteredInNetworkServicingProviders?: number;
  effectiveness?: string | number;
  unfilteredEffectiveness?: string | number;
  efficiency?: string | number;
  unfilteredEfficiency?: string | number;
  motiveAppropriateness?: string | number;
  unfilteredMotiveAppropriateness?: string | number;
  careJourneyCost?: string | number | JSX.Element;
  unfilteredCareJourneyCost?: string | number | JSX.Element;
  careJourneyQuality?: string | number | JSX.Element;
  unfilteredCareJourneyQuality?: string | number | JSX.Element;
}

export type ProviderHubHospitalsFiltersRequestModel = {
  projectId: number;
  projectRunConfigurationId: number;
  searchByOption: ProviderSearchByEnum;
  searchByInput: string;
  regionSetIds: number[];
  specialtyGroupCodes: string[];
  providerNetworkTypes: ProviderNetworkTypeEnum[];
  providerActionStatuses: ProviderActionStatusEnum[];
  closeGapsMin: number;
  closeGapsMax: number;
  effectiveness: number[];
  efficiency: number[];
  motiveScore: number[];
  careJourneyCostScore: number[];
  careJourneyQualityScore: number[];
  effectivenessMin?: number | null;
  effectivenessMax?: number | null;
  efficiencyMin?: number | null;
  efficiencyMax?: number | null;
  motiveScoreMin?: number | null;
  motiveScoreMax?: number | null;
  careJourneyCostScoreMin?: number | null;
  careJourneyCostScoreMax?: number | null;
  careJourneyQualityScoreMin?: number | null;
  careJourneyQualityScoreMax?: number | null;
  includeUnknownEffectiveness?: boolean;
  includeUnknownEfficiency?: boolean;
  includeUnknownMotiveScore?: boolean;
  includeUnknownCareJourneyCostScore?: boolean;
  includeUnknownCareJourneyQualityScore?: boolean;
  servicingProviderCountMinimum: number;
  servicingProviderCountMaximum: number;
  sortHospitalColumnAndOrder: ProviderHubHospitalSortColumn;
  pageSize: number;
  pageIndex?: number;
};

export type ProviderHubFilters = {
  projectId: number;
  projectRunConfigurationId: number;
  viewByOption: ProviderViewByEnum;
  searchByOption: ProviderSearchByEnum;
  searchByInput: string;
  regionSetIds: number[];
  specialtyGroupCodes: DropdownItem<string>[];
  providerNetworkTypes: ProviderNetworkTypeEnum[];
  providerActionStatuses: (ProviderActionStatusEnum | null)[];
  closeGapsMin: number;
  closeGapsMax: number;
  accessPointCountMin: number;
  accessPointCountMax: number;
  effectiveness?: number[];
  efficiency?: number[];
  effectivenessMin?: number;
  effectivenessMax?: number;
  efficiencyMin?: number;
  efficiencyMax?: number;
  includeUnknownEffectiveness?: boolean;
  includeUnknownEfficiency?: boolean;
  includeUnknownMotiveScore?: boolean;
  includeUnknownCareJourneyCostScore?: boolean;
  includeUnknownCareJourneyQualityScore?: boolean;
  locationConfidence: DropdownItem<string>[];
  motiveScore: number[];
  totalClaimsVolume: DropdownItem<string>[];
  totalNewPatientIndicator: DropdownItem<boolean | null>[];
  commercialClaimsVolume: DropdownItem<string>[];
  commercialNewPatientIndicator: DropdownItem<boolean | null>[];
  medicaidClaimsVolume: DropdownItem<string>[];
  medicaidNewPatientIndicator: DropdownItem<boolean | null>[];
  medicareClaimsVolume: DropdownItem<string>[];
  medicareNewPatientIndicator: DropdownItem<boolean | null>[];
  tricareClaimsVolume: DropdownItem<string>[];
  tricareNewPatientIndicator: DropdownItem<boolean | null>[];
  vaClaimsVolume: DropdownItem<string>[];
  vaNewPatientIndicator: DropdownItem<boolean | null>[];
  topProcedures: DropdownItem<string>[];
  topConditions: DropdownItem<string>[];
  topPrescriptions: DropdownItem<string>[];
  networkIntegrityProjectOptionsExclusions: string[];
  networkIntegrityCriticalAlerts: string[];
  networkIntegrityAttestationBasedAlerts: string[];
  networkIntegrityExcessAddressesMin: number;
  networkIntegrityExcessAddressesMax: number;
  networkIntegrityExcessSpecialtiesMin: number;
  networkIntegrityExcessSpecialtiesMax: number;
  pageIndex: number;
  pageSize: number;
  membershipType: MembershipType;
  companionProjectRunConfigurationId: number;
  sortColumnAndOrder: ProviderHubSortColumn;
  sortContractEntityColumnAndOrder: ProviderHubContractEntitySortColumn;
  sortOrganizationColumnAndOrder: ProviderHubOrganizationSortColumn;
  sortHospitalColumnAndOrder: ProviderHubHospitalSortColumn;
  careJourneyCostScoreMin?: number;
  careJourneyCostScoreMax?: number;
  careJourneyQualityScoreMin?: number;
  careJourneyQualityScoreMax?: number;
  careJourneyCostScore?: number[];
  careJourneyQualityScore?: number[];
  motiveScoreMin?: number;
  motiveScoreMax?: number;
  servicingProviderCountMin?: number;
  servicingProviderCountMax?: number;
};

export type ClaimsVolumeFilterFieldKey = "totalClaimsVolume" | "commercialClaimsVolume" | "medicaidClaimsVolume" | "medicareClaimsVolume" | "vaClaimsVolume" | "tricareClaimsVolume";

export type ContractEntityProvidersFilters = {
  regionSetIds: number[],
  pageIndex: number,
  pageSize: number,
  providerNetworkTypes: ProviderNetworkTypeEnum[],
  specialtyGroupCodes: DropdownItem<string>[]
}

export type ContractEntityProvidersFiltersRequestModel = {
  regionSetIds: number[],
  pageIndex: number,
  pageSize: number,
  providerNetworkTypes: ProviderNetworkTypeEnum[],
  // null represents "No Actions Applied" (the backend models that action status as null).
  providerActionStatuses?: (ProviderActionStatusEnum | null)[],
  specialtyGroupCodes: string[],
  sortHospitalProviderColumnAndOrder?: ProviderHubHospitalProviderSortColumn,
  // (Option A parent-pagination): scope the Facilities children query to one CCN parent row,
  // or (otherFacilitiesOnly) to facilities with no resolvable CCN.
  ccn?: string,
  otherFacilitiesOnly?: boolean
}

// (Option A parent-pagination): a CCN (hospital) parent row on the CE Facilities tab "View by: CCN".
// Children (individual facilities) are lazy-loaded per CCN via getContractEntityFacilities with ccn set.
export interface ProviderHubContractEntityCcnGroup {
  ccn?: string;
  hospitalName?: string;
  facilityCount: number;
  accessPointCount: number;
  gapsClosed: number;
  activeGapsClosed?: number;
  pendingGapsClosed?: number;
  approvedGapsClosed?: number;
  opportunityGapsClosed?: number;
  useGapsByRegionStatus?: boolean;
  // Distinct roll-up across the CCN's facilities, for the aggregate CCN parent-row cells.
  providerNetworkTypes?: ProviderNetworkTypeEnum[];
  providerActionStatuses?: ProviderActionStatusEnum[];
  specialtiesImpacted?: ProviderHubSpecialtiesImpacted[];
  countiesImpacted?: ProviderHubCountiesImpacted[];
  // Averaged scores across the CCN's facilities.
  affiliationStrength?: number;
  effectivenessRank?: number;
  efficiencyRank?: number;
  motiveAppropriateness?: number;
  careJourneyCost?: number;
  careJourneyQuality?: number;
}

export interface ProviderHubFiltersRequestModel {
  projectId: number;
  projectRunConfigurationId: number;
  searchByOption: ProviderSearchByEnum;
  searchByInput: string;
  regionSetIds: number[];
  specialtyGroupCodes: string[];
  providerNetworkTypes: ProviderNetworkTypeEnum[];
  providerActionStatuses: ProviderActionStatusEnum[];
  closeGapsMin: number;
  closeGapsMax: number;
  accessPointCountMin: number;
  accessPointCountMax: number;
  effectiveness: number[];
  efficiency: number[];
  totalClaimsVolume: string[];
  totalNewPatientIndicator: (boolean | null)[];
  commercialClaimsVolume: string[];
  commercialNewPatientIndicator: (boolean | null)[];
  medicaidClaimsVolume: string[];
  medicaidNewPatientIndicator: (boolean | null)[];
  medicareClaimsVolume: string[];
  medicareNewPatientIndicator: (boolean | null)[];
  tricareClaimsVolume: string[];
  tricareNewPatientIndicator: (boolean | null)[];
  vaClaimsVolume: string[];
  vaNewPatientIndicator: (boolean | null)[];
  topProcedureCodes: string[];
  topConditionCodes: string[];
  topPrescriptions: string[];
  locationConfidence: string[];
  motiveScore: number[];
  careJourneyCostScore: number[];
  careJourneyQualityScore: number[];
  networkIntegrityProjectOptionsExclusions: string[];
  networkIntegrityCriticalAlerts: string[];
  networkIntegrityAttestationBasedAlerts: string[];
  networkIntegrityExcessAddressesMin: number;
  networkIntegrityExcessAddressesMax: number;
  networkIntegrityExcessSpecialtiesMin: number;
  networkIntegrityExcessSpecialtiesMax: number;
  pageSize: number;
  sortColumnAndOrder: ProviderHubSortColumn;
}

export interface ProviderHubContractEntityFiltersRequestModel {
  projectId: number,
  projectRunConfigurationId: number,
  searchByOption: ProviderSearchByEnum,
  searchByInput: string,
  regionSetIds: number[],
  specialtyGroupCodes: string[],
  providerNetworkTypes: ProviderNetworkTypeEnum[],
  providerActionStatuses: ProviderActionStatusEnum[],
  closeGapsMin: number,
  closeGapsMax: number,
  effectiveness: number[],
  efficiency: number[],
  motiveScore: number[],
  effectivenessMin?: number | null,
  effectivenessMax?: number | null,
  efficiencyMin?: number | null,
  efficiencyMax?: number | null,
  motiveScoreMin?: number | null,
  motiveScoreMax?: number | null,
  careJourneyCostScoreMin?: number | null,
  careJourneyCostScoreMax?: number | null,
  careJourneyQualityScoreMin?: number | null,
  careJourneyQualityScoreMax?: number | null,
  includeUnknownEffectiveness?: boolean,
  includeUnknownEfficiency?: boolean,
  includeUnknownMotiveScore?: boolean,
  includeUnknownCareJourneyCostScore?: boolean,
  includeUnknownCareJourneyQualityScore?: boolean,
  pageSize: number,
  sortContractEntityColumnAndOrder: ProviderHubContractEntitySortColumn,
  careJourneyCostScore: number[],
  careJourneyQualityScore: number[],
  servicingProviderCountMinimum: number,
  servicingProviderCountMaximum: number,
  networkIntegrityProjectOptionsExclusions: string[],
  networkIntegrityCriticalAlerts: string[],
  networkIntegrityAttestationBasedAlerts: string[],
  networkIntegrityExcessAddressesMin: number,
  networkIntegrityExcessAddressesMax: number,
  networkIntegrityExcessSpecialtiesMin: number,
  networkIntegrityExcessSpecialtiesMax: number
}

export interface MaxProviderMetric {
  maxGapsClosed: number;
  maxAccessPointCount: number;
}

export interface MaxContractEntityMetric {
  maxGapsClosed: number;
  servicingProviderCount: number;
}

export interface PciDataPerLOB {
  claimAmount: string;
  claimsVolume: string;
  newPatientIndicator: boolean;
  participation: PCAPayerParticipation[];
  total: string;
}

export enum ProviderHubSortColumn {
  Unknown = "unknown",
  NameAscending = "nameAscending",
  NameDescending = "nameDescending",
  GapsClosedDescending = "gapsClosedDescending",
  GapsClosedAscending = "gapsClosedAscending",
  ActiveGapsClosedDescending = "activeGapsClosedDescending",
  ActiveGapsClosedAscending = "activeGapsClosedAscending",
  PendingGapsClosedDescending = "pendingGapsClosedDescending",
  PendingGapsClosedAscending = "pendingGapsClosedAscending",
  ApprovedGapsClosedDescending = "approvedGapsClosedDescending",
  ApprovedGapsClosedAscending = "approvedGapsClosedAscending",
  OpportunityGapsClosedDescending = "opportunityGapsClosedDescending",
  OpportunityGapsClosedAscending = "opportunityGapsClosedAscending",
  AccessPointCountDescending = "accessPointCountDescending",
  AccessPointCountAscending = "accessPointCountAscending",
  EffectivenessDescending = "effectivenessDescending",
  EffectivenessAscending = "effectivenessAscending",
  EfficiencyDescending = "efficiencyDescending",
  EfficiencyAscending = "efficiencyAscending"
}

export enum ProviderHubOrganizationSortColumn {
  Unknown = "unknown",
  NameAscending = "nameAscending",
  NameDescending = "nameDescending",

  GapsClosedDescending = "gapsClosedDescending",
  GapsClosedAscending = "gapsClosedAscending",

  ActiveGapsClosedDescending = "activeGapsClosedDescending",
  ActiveGapsClosedAscending = "activeGapsClosedAscending",
  PendingGapsClosedDescending = "pendingGapsClosedDescending",
  PendingGapsClosedAscending = "pendingGapsClosedAscending",
  ApprovedGapsClosedDescending = "approvedGapsClosedDescending",
  ApprovedGapsClosedAscending = "approvedGapsClosedAscending",
  OpportunityGapsClosedDescending = "opportunityGapsClosedDescending",
  OpportunityGapsClosedAscending = "opportunityGapsClosedAscending",

  FilteredServicingProvidersDescending = "filteredServicingProvidersDescending",
  FilteredServicingProvidersAscending = "filteredServicingProvidersAscending",

  FilteredServicingSpecialtiesDescending = "filteredServicingSpecialtiesDescending",
  FilteredServicingSpecialtiesAscending = "filteredServicingSpecialtiesAscending",

  FilteredOutOfNetworkServicingProvidersDescending = "filteredOutOfNetworkServicingProvidersDescending",
  FilteredOutOfNetworkServicingProvidersAscending = "filteredOutOfNetworkServicingProvidersAscending",

  FilteredInNetworkServicingProvidersDescending = "filteredInNetworkServicingProvidersDescending",
  FilteredInNetworkServicingProvidersAscending = "filteredInNetworkServicingProvidersAscending",

  FilteredQualityDescending = "filteredQualityDescending",
  FilteredQualityAscending = "filteredQualityAscending",

  FilteredCostDescending = "filteredCostDescending",
  FilteredCostAscending = "filteredCostAscending",

  FilteredMotiveAppropriatenessDescending = "filteredMotiveAppropriatenessDescending",
  FilteredMotiveAppropriatenessAscending = "filteredMotiveAppropriatenessAscending",

  FilteredCareJourneyCostDescending = "filteredCareJourneyCostDescending",
  FilteredCareJourneyCostAscending = "filteredCareJourneyCostAscending",

  FilteredCareJourneyQualityDescending = "filteredCareJourneyQualityDescending",
  FilteredCareJourneyQualityAscending = "filteredCareJourneyQualityAscending"
}

export enum ProviderHubContractEntitySortColumn{
  Unknown = "unknown",
  NameAscending = "nameAscending",
  NameDescending = "nameDescending",

  GapsClosedDescending = "gapsClosedDescending",
  GapsClosedAscending = "gapsClosedAscending",

  ActiveGapsClosedDescending = "activeGapsClosedDescending",
  ActiveGapsClosedAscending = "activeGapsClosedAscending",
  PendingGapsClosedDescending = "pendingGapsClosedDescending",
  PendingGapsClosedAscending = "pendingGapsClosedAscending",
  ApprovedGapsClosedDescending = "approvedGapsClosedDescending",
  ApprovedGapsClosedAscending = "approvedGapsClosedAscending",
  OpportunityGapsClosedDescending = "opportunityGapsClosedDescending",
  OpportunityGapsClosedAscending = "opportunityGapsClosedAscending",

  EntityTypeDescending = "entityTypeDescending",
  EntityTypeAscending = "entityTypeAscending",

  FilteredServicingProvidersDescending = "filteredServicingProvidersDescending",
  FilteredServicingProvidersAscending = "filteredServicingProvidersAscending",

  FilteredServicingSpecialtiesDescending = "filteredServicingSpecialtiesDescending",
  FilteredServicingSpecialtiesAscending = "filteredServicingSpecialtiesAscending",

  FilteredOutOfNetworkServicingProvidersDescending = "filteredOutOfNetworkServicingProvidersDescending",
  FilteredOutOfNetworkServicingProvidersAscending = "filteredOutOfNetworkServicingProvidersAscending",

  FilteredInNetworkServicingProvidersDescending = "filteredInNetworkServicingProvidersDescending",
  FilteredInNetworkServicingProvidersAscending = "filteredInNetworkServicingProvidersAscending",

  FilteredQualityDescending = "filteredQualityDescending",
  FilteredQualityAscending = "filteredQualityAscending",

  FilteredCostDescending = "filteredCostDescending",
  FilteredCostAscending = "filteredCostAscending",

  FilteredMotiveAppropriatenessDescending = "filteredMotiveAppropriatenessDescending",
  FilteredMotiveAppropriatenessAscending = "filteredMotiveAppropriatenessAscending",

  FilteredCareJourneyCostDescending = "filteredCareJourneyCostDescending",
  FilteredCareJourneyCostAscending = "filteredCareJourneyCostAscending",

  FilteredCareJourneyQualityDescending = "filteredCareJourneyQualityDescending",
  FilteredCareJourneyQualityAscending = "filteredCareJourneyQualityAscending"
}

export enum ProviderHubHospitalSortColumn {
  Unknown = "unknown",
  NameAscending = "nameAscending",
  NameDescending = "nameDescending",
  GapImpactAscending = "gapImpactAscending",
  GapImpactDescending = "gapImpactDescending",
  ActiveGapImpactDescending = "activeGapImpactDescending",
  ActiveGapImpactAscending = "activeGapImpactAscending",
  PendingGapImpactDescending = "pendingGapImpactDescending",
  PendingGapImpactAscending = "pendingGapImpactAscending",
  ApprovedGapImpactDescending = "approvedGapImpactDescending",
  ApprovedGapImpactAscending = "approvedGapImpactAscending",
  OpportunityGapImpactDescending = "opportunityGapImpactDescending",
  OpportunityGapImpactAscending = "opportunityGapImpactAscending",
  FilteredServicingProvidersDescending = "filteredServicingProvidersDescending",
  FilteredServicingProvidersAscending = "filteredServicingProvidersAscending",
  FilteredServicingSpecialtiesDescending = "filteredServicingSpecialtiesDescending",
  FilteredServicingSpecialtiesAscending = "filteredServicingSpecialtiesAscending",
  FilteredOutOfNetworkServicingProvidersDescending = "filteredOutOfNetworkServicingProvidersDescending",
  FilteredOutOfNetworkServicingProvidersAscending = "filteredOutOfNetworkServicingProvidersAscending",
  FilteredInNetworkServicingProvidersDescending = "filteredInNetworkServicingProvidersDescending",
  FilteredInNetworkServicingProvidersAscending = "filteredInNetworkServicingProvidersAscending",
  FilteredQualityDescending = "filteredQualityDescending",
  FilteredQualityAscending = "filteredQualityAscending",
  FilteredCostDescending = "filteredCostDescending",
  FilteredCostAscending = "filteredCostAscending",
  FilteredMotiveAppropriatenessDescending = "filteredMotiveAppropriatenessDescending",
  FilteredMotiveAppropriatenessAscending = "filteredMotiveAppropriatenessAscending",
  FilteredCareJourneyCostDescending = "filteredCareJourneyCostDescending",
  FilteredCareJourneyCostAscending = "filteredCareJourneyCostAscending",
  FilteredCareJourneyQualityDescending = "filteredCareJourneyQualityDescending",
  FilteredCareJourneyQualityAscending = "filteredCareJourneyQualityAscending"
}

export enum ProviderHubHospitalProviderSortColumn {
  Unknown = "unknown",
  NameAscending = "nameAscending",
  NameDescending = "nameDescending",
  NpiAscending = "npiAscending",
  NpiDescending = "npiDescending",
  AffiliationStrengthAscending = "affiliationStrengthAscending",
  AffiliationStrengthDescending = "affiliationStrengthDescending",
  GapsClosedAscending = "gapsClosedAscending",
  GapsClosedDescending = "gapsClosedDescending",
  EffectivenessAscending = "effectivenessAscending",
  EffectivenessDescending = "effectivenessDescending",
  EfficiencyAscending = "efficiencyAscending",
  EfficiencyDescending = "efficiencyDescending",
  TotalClaimsAscending = "totalClaimsAscending",
  TotalClaimsDescending = "totalClaimsDescending",
  MotiveAscending = "motiveAscending",
  MotiveDescending = "motiveDescending",
  CareJourneyCostAscending = "careJourneyCostAscending",
  CareJourneyCostDescending = "careJourneyCostDescending",
  CareJourneyQualityAscending = "careJourneyQualityAscending",
  CareJourneyQualityDescending = "careJourneyQualityDescending",
  ContractEntityAscending = "contractEntityAscending",
  ContractEntityDescending = "contractEntityDescending",
  OrganizationAscending = "organizationAscending",
  OrganizationDescending = "organizationDescending",
  ActiveGapsClosedAscending = "activeGapsClosedAscending",
  ActiveGapsClosedDescending = "activeGapsClosedDescending",
  PendingGapsClosedAscending = "pendingGapsClosedAscending",
  PendingGapsClosedDescending = "pendingGapsClosedDescending",
  ApprovedGapsClosedAscending = "approvedGapsClosedAscending",
  ApprovedGapsClosedDescending = "approvedGapsClosedDescending",
  OpportunityGapsClosedAscending = "opportunityGapsClosedAscending",
  OpportunityGapsClosedDescending = "opportunityGapsClosedDescending"
}

export type SetFilterAction<T extends keyof ProviderHubFilters> = {
  type: "SET_FILTER";
  key: T;
  value: ProviderHubFilters[T];
};

export type FiltersAction =
  | SetFilterAction<keyof ProviderHubFilters>
  | { type: "RESET_FILTERS" };

export interface AiChatRequest {
  projectRunConfigurationId: number;
  threadId?: string;
  messages: AiChatMessage[];
}

export interface AiChatResponse {
  messages: AiChatMessage[];
  threadId: string;
}

export interface AiChatPaginatedTableResult {
  items: string[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  /** Maps display column names (e.g. "Provider Name") to SQL column names (e.g. "PROVIDER_NAME") */
  columnMap: Record<string, string>;
  /** How to render each column, aligned by index with the header row in `items`. */
  columnFormats?: AiChatColumnFormat[];
}

export interface AiChatMessage {
  content: string[];
  messageType: AiChatMessageType;
  timestamp: Date;
  responseId?: number;
  dataIsTruncated?: boolean;
  chartHeader?: string;
  howIFound?: string;
  userRating?: AiResponseReviewRating;
  excludeFromPromptLimits?: boolean;
  /** Table messages only: how to render each column, aligned by index with the header row in `content`. */
  columnFormats?: AiChatColumnFormat[];
}

/** Per-column render format decided by the backend (AiChatColumnFormatRegistry). */
export enum AiChatColumnFormat {
  None = "none",
  Currency = "currency"
}

export enum AiChatMessageType {
  Unknown = "unknown",
  UserPrompt = "userPrompt",
  ResponseText = "responseText",
  Table = "table",
  BarChart = "barChart",
  LineChart = "lineChart",
  PieChart = "pieChart",
  System = "system"
}

export enum ProviderSearchByEnum {
  Name = "Name",
  NPI = "NPI",
  TIN = "TIN",
  ContractEntity = "Contract Entity",
  NPI2 = "NPI2",
  CCN = "CCN"
}

export enum ProviderViewByEnum {
  Provider = "Provider",
  ContractEntity = "Contract Entity",
  Organization = "Organization",
  Hospital = "Hospital"
}

export enum ProviderHubViewEnum {
  EmptyState = "emptyState",
  Providers = "providers",
  ProviderProfile = "providerProfile",
  ProviderClaimsInsights = "providerClaimsInsights",
  ContractEntity = "contractEntity",
  ContractEntityProfile = "contractEntityProfile",
  Organization = "organization",
  OrganizationProfile = "organizationProfile",
  Hospital = "hospital",
  HospitalProfile = "hospitalProfile"
}

export enum AiChatChartTypes {
  None = "none",
  Table = "table",
  Bar = "bar",
  Line = "line",
  Pie = "pie"
}

export enum YesNoInsufficientEnum {
  Yes = "Yes",
  No = "No",
  Insufficient = "Insufficient"
}

export enum ProviderProfileActionEnum {
  Unknown = "Unknown",
  Target = "Target",
  Exclude = "Exclude",
  Terminate = "Terminate"
}

export enum ProviderProfileActionLevelEnum {
  Unknown = "Unknown",
  AccessPoint = "AccessPoint",
  AllAccessPoints = "AllAccessPoints",
  Provider = "Provider"
}

export interface VirtualAssistantContext {
  servicingProviderKey?: string;
  servicingProviderName?: string;
  contractEntityId?: number;
  contractEntityName?: string;
  isOpen?: boolean;
}

export interface ProviderProfileAffiliation {
  [wafflesKeyColumnName]: string;
  name: string;
  type: string;
  npi: string;
  entitySize: string;
  affiliationStrength: string;
  primary: string;
}

export interface ProviderHubSpecialtyView {
  [wafflesKeyColumnName]: string;
  specialty: string;
  providerCount: number;
}

export interface ProviderCountPerClaimsVolumePerLOB {
  lineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum;
  claimsVolumeLabel: ClaimsActivityVolumeLabelEnum;
  providerCount: number;
}

export interface SpecialtyProviderCountModel {
  specialtyCode: string;
  specialtyName: string;
  providerCount: number;
}

export interface ReadyForReviewModel {
  networkExpansionOpportunities: SpecialtyProviderCountModel[];
  networkExpansionOpportunitiesTotalCount: number;
  underperformingProviders: SpecialtyProviderCountModel[];
  underperformingProvidersTotalCount: number;
}

export interface ScoreProviderCountModel {
  /** Integer 0-5, 0 representing "Unknown". */
  score: number;
  providerCount: number;
}

export interface EffectivenessEfficiencyBreakdown {
  effectiveness: ScoreProviderCountModel[];
  efficiency: ScoreProviderCountModel[];
}
